// src/inventory/InventoryRoutes.jsx
// React Router v6 routes for all 19 Inventory Management pages
// Usage: Import inside your main dashboard router
// Mount at: /dashboard/inventory
// ─────────────────────────────────────────────────────────────────────────────
import React, { Suspense } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import Categories from '../pages/inventory/CategoriesPage';
import SubCategoriesPage from '../pages/inventory/SubCategoriesPage';
import BrandsPage from '../pages/inventory/BrandsPage';
import UnitsPage from '../pages/inventory/UnitsPage';
import ProductsPage from '../pages/inventory/ProductsPage';
import StockManagementPage from '../pages/inventory/StockManagementPage';
import StockInPage from '../pages/inventory/StockInPage';
import StockOutPage from '../pages/inventory/StockOutPage';
import StockTransferPage from '../pages/inventory/StockTransferPage';
import StockAdjustmentPage from '../pages/inventory/StockAdjustmentPage';
import SuppliersPage from '../pages/inventory/SuppliersPage';
import PurchaseOrdersPage from '../pages/inventory/PurchaseOrdersPage';
import PurchaseReturnsPage from '../pages/inventory/PurchaseReturnsPage';
import WarehousesPage from '../pages/inventory/WarehousesPage';
import LocationsPage from '../pages/inventory/LocationsPage';
import LowStockPage from '../pages/inventory/LowStockPage';
import ExpiryAlertsPage from '../pages/inventory/ExpiryAlertsPage';
import ReportsPage from '../pages/inventory/ReportsPage';
import BarcodePage from '../pages/inventory/BarcodePage';




// ─────────────────────────────────────────────────────────────────────────────
// Loading fallback
// ─────────────────────────────────────────────────────────────────────────────
const PageLoader = () => (
  <div style={{ display:'flex', alignItems:'center', justifyContent:'center', height:'60vh', flexDirection:'column', gap:12 }}>
    <div style={{ width:36, height:36, border:'3px solid #e5e7eb', borderTop:'3px solid #1a56db', borderRadius:'50%', animation:'spin .8s linear infinite' }} />
    <div style={{ fontSize:13, color:'#6b7280' }}>Loading...</div>
    <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
  </div>
);

// ─────────────────────────────────────────────────────────────────────────────
// INVENTORY ROUTES COMPONENT
// Props:
//   companyId — required, scopes all data fetching per company
// ─────────────────────────────────────────────────────────────────────────────
const InventoryRoutes = ({ companyId }) => {
  const p = (Component) => <Component companyId={companyId} />;

  return (
    <Suspense fallback={<PageLoader />}>
      <Routes>
        {/* Default redirect to stock overview */}
        <Route index element={<Navigate to="stock" replace />} />

        {/* ── Product Catalog ──────────────────────────────────────────── */}
        {/* GET /dashboard/inventory/categories */}
        <Route path="categories"      element={p(Categories)} />

        {/* GET /dashboard/inventory/sub-categories */}
        <Route path="sub-categories"  element={p(SubCategoriesPage)} />

        {/* GET /dashboard/inventory/brands */}
        <Route path="brands"          element={p(BrandsPage)} />

        {/* GET /dashboard/inventory/units */}
        <Route path="units"           element={p(UnitsPage)} />

        {/* GET /dashboard/inventory/products */}
        <Route path="products"        element={p(ProductsPage)} />

        {/* ── Stock Operations ─────────────────────────────────────────── */}
        {/* GET /dashboard/inventory/stock */}
        <Route path="stock"           element={p(StockManagementPage)} />

        {/* GET /dashboard/inventory/stock-in */}
        <Route path="stock-in"        element={p(StockInPage)} />

        {/* GET /dashboard/inventory/stock-out */}
        <Route path="stock-out"       element={p(StockOutPage)} />

        {/* GET /dashboard/inventory/transfer */}
        <Route path="transfer"        element={p(StockTransferPage)} />

        {/* GET /dashboard/inventory/adjustment */}
        <Route path="adjustment"      element={p(StockAdjustmentPage)} />

        {/* ── Procurement ─────────────────────────────────────────────── */}
        {/* GET /dashboard/inventory/suppliers */}
        <Route path="suppliers"       element={p(SuppliersPage)} />

        {/* GET /dashboard/inventory/purchase-orders */}
        <Route path="purchase-orders" element={p(PurchaseOrdersPage)} />

        {/* GET /dashboard/inventory/purchase-returns */}
        <Route path="purchase-returns"element={p(PurchaseReturnsPage)} />

        {/* ── Warehousing ──────────────────────────────────────────────── */}
        {/* GET /dashboard/inventory/warehouses */}
        <Route path="warehouses"      element={p(WarehousesPage)} />

        {/* GET /dashboard/inventory/locations */}
        <Route path="locations"       element={p(LocationsPage)} />

        {/* ── Alerts & Reports ─────────────────────────────────────────── */}
        {/* GET /dashboard/inventory/low-stock */}
        <Route path="low-stock"       element={p(LowStockPage)} />

        {/* GET /dashboard/inventory/expiry-alerts */}
        <Route path="expiry-alerts"   element={p(ExpiryAlertsPage)} />

        {/* GET /dashboard/inventory/reports */}
        <Route path="reports"         element={p(ReportsPage)} />

        {/* GET /dashboard/inventory/barcode */}
        <Route path="barcode"         element={p(BarcodePage)} />

        {/* 404 fallback */}
        <Route path="*" element={<Navigate to="stock" replace />} />
      </Routes>
    </Suspense>
  );
};

export default InventoryRoutes;